import { ContexteTravailRequisError, IdentifiantsInvalidesError, getApi } from '@/lib/api';
import { enregistrerSession } from '@/lib/session';
import { Alert, Button, LoadingRing, Logo } from '@pms-digital/design-system';
import { identifiantsSchema } from '@rosetta/shared';
import { type FormEvent, useId, useState } from 'react';
import { AlerteLogin } from './alerte-login';
import { CaseSeSouvenir } from './case-se-souvenir';
import { ChampLogin } from './champ-login';
import { ChampMotDePasse } from './champ-mot-de-passe';

export const MESSAGE_IDENTIFIANTS_INVALIDES = 'E-mail ou mot de passe incorrect.';
export const MESSAGE_SERVICE_INDISPONIBLE = 'Le service est momentanément indisponible, réessayez.';
export const MESSAGE_CONTEXTE_REQUIS =
  'Votre compte a plusieurs contextes de travail : la configuration de Rosetta doit en désigner un.';
const MESSAGE_EMAIL_VIDE = 'Saisissez votre adresse e-mail.';
const MESSAGE_MOT_DE_PASSE_VIDE = 'Saisissez votre mot de passe.';
const MESSAGE_CHAMPS_VIDES = 'Saisissez votre adresse e-mail et votre mot de passe.';

type NomChamp = 'email' | 'motDePasse';

/** What the alert says when the form is not valid, field by field. */
function messageChampsInvalides(champs: ReadonlySet<NomChamp>): string {
  if (champs.has('email') && champs.has('motDePasse')) return MESSAGE_CHAMPS_VIDES;
  return champs.has('email') ? MESSAGE_EMAIL_VIDE : MESSAGE_MOT_DE_PASSE_VIDE;
}

/** What the alert says when the login fails for another reason than the form. */
function messageErreurConnexion(erreur: unknown): string {
  if (erreur instanceof IdentifiantsInvalidesError) return MESSAGE_IDENTIFIANTS_INVALIDES;
  if (erreur instanceof ContexteTravailRequisError) return MESSAGE_CONTEXTE_REQUIS;
  return MESSAGE_SERVICE_INDISPONIBLE;
}

/** Said over the form when the user was sent back here by an expired session (#13). */
export const MESSAGE_SESSION_EXPIREE = 'Votre session a expiré, reconnectez-vous.';

interface LoginCardProps {
  /** Called once the session is stored: the page then leaves `/login`. */
  onConnecte: () => void;
  /** The user comes from a page whose session expired (#13). */
  sessionExpiree?: boolean;
}

/**
 * The login panel (L1, L2): logo, title, alert, then the form. The inputs are
 * uncontrolled; the submit reads them with `FormData` and validates them with the
 * shared `identifiantsSchema`.
 *
 * A refusal never says which of the e-mail and the password is wrong. Any other
 * failure (service unreachable, unexpected answer) gets its own, equally plain, alert. A user
 * sent back by an expired session reads "Votre session a expiré, reconnectez-vous." in a note
 * over the form until a first attempt (#13); it is a status, not an error.
 */
export function LoginCard({ onConnecte, sessionExpiree = false }: LoginCardProps) {
  const idAlerte = useId();
  const [enCours, setEnCours] = useState(false);
  // `cle` changes on every failure so the alert is inserted again, and read again by
  // assistive technologies, even when the text is the one already shown.
  const [alerte, setAlerte] = useState<{ message: string; cle: number } | null>(null);
  const [champsInvalides, setChampsInvalides] = useState<ReadonlySet<NomChamp>>(new Set());

  function signaler(message: string) {
    setAlerte((courante) => ({ message, cle: (courante?.cle ?? 0) + 1 }));
  }

  async function envoyer(evenement: FormEvent<HTMLFormElement>) {
    evenement.preventDefault();
    if (enCours) return;

    const formulaire = evenement.currentTarget;
    const donnees = new FormData(formulaire);
    const verdict = identifiantsSchema.safeParse({
      email: donnees.get('email'),
      motDePasse: donnees.get('motDePasse'),
    });

    if (!verdict.success) {
      const invalides = new Set<NomChamp>();
      for (const probleme of verdict.error.issues) {
        const nom = probleme.path[0];
        if (nom === 'email' || nom === 'motDePasse') invalides.add(nom);
      }
      setChampsInvalides(invalides);
      signaler(messageChampsInvalides(invalides));
      const premier = invalides.has('email') ? 'email' : 'motDePasse';
      const champ = formulaire.elements.namedItem(premier);
      if (champ instanceof HTMLInputElement) champ.focus();
      return;
    }

    setChampsInvalides(new Set());
    setAlerte(null);
    setEnCours(true);
    try {
      const session = await getApi().login(verdict.data);
      enregistrerSession(session, donnees.get('seSouvenir') !== null);
      // The button stays inactive until the page has left: no second submit.
      onConnecte();
    } catch (erreur) {
      signaler(messageErreurConnexion(erreur));
      setEnCours(false);
    }
  }

  return (
    <div className="flex w-full flex-col rounded-lg bg-white px-12 pt-12 pb-12 shadow-lg">
      <div className="mb-8 flex justify-center">
        <Logo width={252} />
      </div>
      <h1 className="text-center font-bold text-h3 leading-[1.2] tracking-normal">Se connecter</h1>
      <p className="mt-1 text-center font-light text-body text-gray-600">accédez à rosetta</p>

      {alerte === null ? (
        sessionExpiree ? (
          <div className="mt-6">
            <Alert tone="info">{MESSAGE_SESSION_EXPIREE}</Alert>
          </div>
        ) : null
      ) : (
        <AlerteLogin key={alerte.cle} id={idAlerte} message={alerte.message} />
      )}

      <form onSubmit={envoyer} noValidate className="mt-8 flex flex-col gap-4">
        <ChampLogin
          libelle="adresse e-mail"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="vous@exemple.fr"
          invalide={champsInvalides.has('email')}
          aria-describedby={champsInvalides.has('email') ? idAlerte : undefined}
        />
        {/* No placeholder (#13): dots in an empty field read as a password already typed. */}
        <ChampMotDePasse
          libelle="mot de passe"
          name="motDePasse"
          autoComplete="current-password"
          invalide={champsInvalides.has('motDePasse')}
          aria-describedby={champsInvalides.has('motDePasse') ? idAlerte : undefined}
        />

        {/* "mot de passe oublié ?" was a button with no screen behind it: the help line under the
            panel says what to do instead (#13). */}
        <CaseSeSouvenir defaultChecked />

        <div className="mt-1">
          <Button
            type="submit"
            variant="secondary"
            fullWidth
            disabled={enCours}
            aria-busy={enCours}
          >
            {enCours ? (
              <>
                <LoadingRing size="sm" />
                connexion...
              </>
            ) : (
              'se connecter'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
